// ConsumableSelectorModal.jsx
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { consumableService } from '../../../services/supabaseService';
import { supabase } from '../../../lib/supabase';

const ConsumableSelectorModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  existingConsumables = [],
  disabled = false ,
  user
}) => {
  const [consumables, setConsumables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loadingError, setLoadingError] = useState(null);

  // Charger la liste des consommables disponibles
  useEffect(() => {
    if (isOpen) {
      loadConsumables();
    }
  }, [isOpen]);

  // Réinitialiser la sélection quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setSelectedItems([]);
      setSearchTerm('');
      setLoadingError(null);
    }
  }, [isOpen]);

  const loadConsumables = async () => {
    try {
      setLoading(true);
      setLoadingError(null);
      
      // Appel au service pour récupérer les consommables
      let data;
      if (consumableService && consumableService.getAllConsumables) {
        // 1. Récupérer tous les consommables
        const response = await consumableService.getAllConsumables();
        const allConsumables = response?.data || [];
        
        // 2. Récupérer les IDs et quantités du technicien
        const { data: techConsumables, error } = await supabase
          ?.from('consumables_techniciens')
          .select('consumable, quantity')
          .eq('technicien', user?.id);
        
        if (error) {
          console.error("Erreur lors de la récupération des quantités:", error);
        }
        
        // 3. Créer un Map pour un accès rapide ID -> quantité
        const quantityMap = new Map();
        techConsumables?.forEach(item => {
          quantityMap.set(item.consumable, item.quantity);
        });
        
        // 4. Filtrer et enrichir les consommables avec leur quantité
        data = allConsumables
          .filter(item => quantityMap.has(item.id))
          .map(item => ({
            ...item,
            quantity: quantityMap.get(item.id) || 0 // Ajout de la quantité
          }));
        
      } else {
        // Données de démonstration si le service n'existe pas
        data = [
          {
            id: 1,
            name: "Tournevis cruciforme",
            category: "Outillage",
            unit: "pièce",
            price: 12.50,
            quantity: 5
          },
          {
            id: 2,
            name: "Pince coupante",
            category: "Outillage", 
            unit: "pièce",
            price: 18.90,
            quantity: 3
          },
          {
            id: 3,
            name: "Multimètre numérique",
            category: "Mesure",
            unit: "pièce",
            price: 45.00,
            quantity: 2
          },
          {
            id: 4,
            name: "Rouleau de ruban adhésif",
            category: "Fournitures",
            unit: "rouleau",
            price: 3.50,
            quantity: 10
          },
          {
            id: 5,
            name: "Vis 5x20mm (boîte de 100)",
            category: "Quincaillerie",
            unit: "boîte",
            price: 8.75,
            quantity: 4
          }
        ];
      }
      
      setConsumables(data);
    } catch (error) {
      console.error('Erreur lors du chargement des consommables:', error);
      setLoadingError('Impossible de charger la liste des consommables');
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les consommables par recherche et exclure ceux déjà sélectionnés
  const filteredConsumables = consumables.filter(item => {
    // Filtre par recherche
    const matchesSearch = searchTerm === '' || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.category && item.category.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  // Grouper par catégorie
  const groupedByCategory = filteredConsumables.reduce((acc, item) => {
    const category = item.category || 'Autres';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  // Trier les catégories
  const sortedCategories = Object.keys(groupedByCategory).sort();

  const handleToggleItem = (item) => {
    setSelectedItems(prev => {
      const isSelected = prev.some(i => i.id === item.id);
      if (isSelected) {
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedItems(filteredConsumables);
  };

  const handleDeselectAll = () => {
    setSelectedItems([]);
  };

  const handleConfirm = () => {
    if (selectedItems.length > 0) {
      onSelect(selectedItems);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={disabled ? undefined : onClose}
      />
      
      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
          
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <Icon name="Package" size={20} className="text-primary" />
              <h2 className="text-lg font-semibold text-foreground">
                Sélectionner des consommables
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-colors"
              disabled={disabled}
            >
              <Icon name="X" size={20} />
            </button>
          </div>
          
          {/* Search Bar */}
          <div className="p-4 border-b border-border">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="Search" size={16} className="text-muted-foreground" />
              </div>
              <Input
                type="text"
                placeholder="Rechercher un consommable..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                disabled={disabled || loading}
              />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                <p className="text-muted-foreground">Chargement des consommables...</p>
              </div>
            ) : loadingError ? (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                <div className="flex items-center gap-2 text-error">
                  <Icon name="AlertCircle" size={20} />
                  <p className="font-medium">{loadingError}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadConsumables}
                  className="mt-3"
                  iconName="RefreshCw"
                >
                  Réessayer
                </Button>
              </div>
            ) : filteredConsumables.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Package" size={48} className="text-muted-foreground mb-3 mx-auto" />
                <p className="text-muted-foreground font-medium">Aucun consommable trouvé</p>
                {searchTerm && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Essayez d'autres termes de recherche
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Actions de sélection */}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    {selectedItems.length} sélectionné(s)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectAll}
                      disabled={disabled}
                    >
                      Tout sélectionner
                    </Button>
                    {selectedItems.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDeselectAll}
                        disabled={disabled}
                      >
                        Tout désélectionner
                      </Button>
                    )}
                  </div>
                </div>

                {/* Liste par catégories */}
                {sortedCategories.map(category => (
                  <div key={category} className="space-y-2">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2">
                      {category}
                    </h3>
                    <div className="bg-muted/30 rounded-lg divide-y divide-border">
                      {groupedByCategory[category].map(item => {
                        const isSelected = selectedItems.some(i => i.id === item.id);
                        const isInExisting = existingConsumables.some(i => i.id === item.id);
                        
                        return (
                          <div
                            key={item.id}
                            className={`flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors ${
                              isSelected ? 'bg-primary/5' : ''
                            } ${isInExisting ? 'opacity-50' : ''}`}
                          >
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleToggleItem(item)}
                              disabled={disabled || isInExisting}
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-foreground">
                                  {item.name}
                                </p>
                                {/* AFFICHAGE DE LA QUANTITÉ DU TECHNICIEN */}
                                {item.quantity !== undefined && (
                                  <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-full">
                                      <Icon name="Layers" size={12} />
                                      <span className="text-xs font-medium">{item.quantity}</span>
                                    </div>
                                    {item.price && (
                                      <span className="text-xs text-muted-foreground">
                                        {item.price.toFixed(2)} €
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-muted-foreground">
                                  Unité: {item.unit || '-'}
                                </span>
                                {isInExisting && (
                                  <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                                    Déjà ajouté
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-border bg-muted/30 rounded-b-lg">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                {selectedItems.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{selectedItems.length}</span> consommable(s) sélectionné(s)
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={disabled}
                >
                  Annuler
                </Button>
                <Button
                  variant="default"
                  onClick={handleConfirm}
                  disabled={disabled || selectedItems.length === 0}
                >
                  Ajouter ({selectedItems.length})
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant Checkbox simplifié
const Checkbox = ({ checked, onChange, disabled }) => {
  return (
    <div 
      className={`flex items-center justify-center w-5 h-5 rounded border transition-colors ${
        checked 
          ? 'bg-primary border-primary text-white' 
          : 'bg-white border-border hover:border-primary'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      onClick={disabled ? undefined : onChange}
    >
      {checked && (
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20 6L9 17l-5-5-1.5 1.5L9 20 21.5 7.5z" />
        </svg>
      )}
    </div>
  );
};

export default ConsumableSelectorModal;